import { SsoConnectionUser, SsoRoleMapping } from '@choco-admin/types';
import { formatDateWithPreset } from '@choco-admin/utils';
import { Button, Divider, TableCell, TableRow } from '@heroui/react';
import React, { useMemo, useState } from 'react';
import { FiPlus, FiShield, FiTrash2 } from 'react-icons/fi';

import { ErrorCard } from '../../../../components/ErrorCard';
import { SUPPLIER_ROLE_LABELS } from '../../../../components/SupplierRoleSelect';
import {
  type LoadingTableColumn,
  LinkCell,
  LoadingTable,
} from '../../../../components/Table';
import { useSsoRoleMappings } from '../../hooks';
import { shortenId } from '../../utils';

import { CreateRoleMappingModal } from './CreateRoleMappingModal';
import { DeleteRoleMappingModal } from './DeleteRoleMappingModal';
import { ColumnLabel, InfoTooltip } from './InfoTooltip';

const SECTION_EXPLANATION = [
  'These are the roles this provider can send, and what each one gives access to. A role that is not listed here gives no access at all.',
  'If somebody arrives with more than one of these roles, Choco uses the first one that works. There is no order and no priority, so avoid mapping two roles that lead somewhere different.',
];

const FEDERATED_ROLE_EXPLANATION = [
  "The role name the customer's own directory sends when somebody signs in. It is their wording, not Choco's, so type it exactly as they send it.",
];

const DISTRIBUTOR_EXPLANATION = [
  'Anybody who signs in with this role gets access to this distributor.',
  "They send messages as the distributor's bot user, which Choco works out from the distributor itself. You never pick it.",
];

const CHOCO_ROLE_EXPLANATION = [
  'What the person becomes on that distributor: an admin or a sales rep. It is the same pair of roles you see on a distributor\u2019s team.',
];

const columns: LoadingTableColumn[] = [
  {
    label: 'Role from the provider',
    width: '34%',
    className: 'pl-6',
    headerRender: () => (
      <ColumnLabel
        label="Role from the provider"
        paragraphs={FEDERATED_ROLE_EXPLANATION}
      />
    ),
  },
  {
    label: 'Distributor',
    width: '26%',
    className: 'whitespace-nowrap',
    headerRender: () => (
      <ColumnLabel label="Distributor" paragraphs={DISTRIBUTOR_EXPLANATION} />
    ),
  },
  {
    label: 'Role in Choco',
    width: '14%',
    className: 'whitespace-nowrap',
    headerRender: () => (
      <ColumnLabel label="Role in Choco" paragraphs={CHOCO_ROLE_EXPLANATION} />
    ),
  },
  {
    label: 'Created',
    width: '14%',
    className: 'hidden lg:table-cell whitespace-nowrap',
  },
  { label: 'Actions', width: '10%', className: 'pr-6 whitespace-nowrap' },
];

export interface RoleMappingsSectionProps {
  connectionId: string;
  connectionName: string;
  /** Everyone on the connection, so a removal can say who it affects. */
  users: SsoConnectionUser[];
  /** False while the user list is still loading or was refused. */
  isUserCountKnown: boolean;
}

const formatDate = (value?: string): string =>
  value ? formatDateWithPreset(value, 'DAY_FIRST_MEDIUM') : '—';

/**
 * The roles this connection's directory can assert, and what each one grants.
 *
 * Distinct from the Roles column on the users table: that shows what a person
 * arrived with, this shows what the connection knows how to honour. A role
 * nobody has mapped resolves to nothing.
 */
export const RoleMappingsSection: React.FC<RoleMappingsSectionProps> = ({
  connectionId,
  connectionName,
  users,
  isUserCountKnown,
}) => {
  const { roleMappings, isLoading, error, refetch } = useSsoRoleMappings({
    connectionId,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<SsoRoleMapping | null>(
    null
  );

  const holdersByRole = useMemo(() => {
    const counts = new Map<string, number>();

    users.forEach((user) => {
      user.federatedRoles.forEach((role) => {
        counts.set(role, (counts.get(role) ?? 0) + 1);
      });
    });

    return counts;
  }, [users]);

  const visibleMappings = useMemo(
    () =>
      [...roleMappings].sort((a, b) =>
        a.federatedRole.localeCompare(b.federatedRole)
      ),
    [roleMappings]
  );

  const renderRow = (roleMapping: SsoRoleMapping) => (
    <TableRow key={roleMapping.federatedRole}>
      <TableCell className="pl-6">
        <span className="block break-all font-mono text-xs text-foreground">
          {roleMapping.federatedRole}
        </span>
      </TableCell>
      <TableCell>
        <div className="space-y-0.5">
          {roleMapping.supplierName ? (
            <LinkCell
              to={`/suppliers/${roleMapping.supplierId}`}
              newWindow
              className="block break-words"
            >
              {roleMapping.supplierName}
            </LinkCell>
          ) : (
            <span className="text-default-500">Distributor not found</span>
          )}
          {/* Same pair as the picker and the distributor profile, because names
              repeat and differ only by market. */}
          <p className="break-all font-mono text-xs text-default-400">
            {roleMapping.supplierReferenceId
              ? `Ref ${roleMapping.supplierReferenceId} · `
              : ''}
            {shortenId(roleMapping.supplierId)}
          </p>
        </div>
      </TableCell>
      <TableCell className="text-default-500">
        {roleMapping.chocoSupplierRole
          ? SUPPLIER_ROLE_LABELS[roleMapping.chocoSupplierRole]
          : '—'}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-default-500">
        {formatDate(roleMapping.createdAt)}
      </TableCell>
      <TableCell className="pr-6">
        <Button
          isIconOnly
          size="sm"
          variant="flat"
          color="danger"
          className="min-w-8"
          aria-label={`Remove the mapping for ${roleMapping.federatedRole}`}
          onPress={() => setPendingDeletion(roleMapping)}
        >
          <FiTrash2 className="h-3 w-3" />
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <div className="pt-2">
        <Divider className="mb-6" />
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            Role mappings
            <InfoTooltip
              label="What role mappings do"
              paragraphs={SECTION_EXPLANATION}
            />
            {!isLoading && (
              <span className="text-sm font-normal text-default-500">
                {visibleMappings.length}
              </span>
            )}
          </h2>
          <Button
            size="sm"
            color="primary"
            startContent={<FiPlus className="h-4 w-4" />}
            onPress={() => setIsCreating(true)}
          >
            Add role mapping
          </Button>
        </div>
      </div>

      {error ? (
        <ErrorCard
          title="Could not load role mappings"
          message={error.message}
          onRetry={refetch}
        />
      ) : (
        <LoadingTable<SsoRoleMapping>
          columns={columns}
          ariaLabel="SSO role mappings"
          isLoading={isLoading}
          isEmpty={!isLoading && visibleMappings.length === 0}
          emptyState={{
            icon: FiShield,
            title: 'No role mappings',
            description:
              'Roles this provider sends give no access until one is mapped here.',
          }}
          data={visibleMappings}
          renderRow={renderRow}
        />
      )}

      {isCreating && (
        <CreateRoleMappingModal
          isOpen
          onClose={() => setIsCreating(false)}
          connectionId={connectionId}
          connectionName={connectionName}
          existingRoles={roleMappings.map(
            (roleMapping) => roleMapping.federatedRole
          )}
        />
      )}

      <DeleteRoleMappingModal
        roleMapping={pendingDeletion}
        onClose={() => setPendingDeletion(null)}
        affectedUserCount={
          pendingDeletion
            ? (holdersByRole.get(pendingDeletion.federatedRole) ?? 0)
            : 0
        }
        isCountKnown={isUserCountKnown}
      />
    </>
  );
};
